import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { PWAUpdateNotification } from "@/components/PWAUpdateNotification";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import PlatformPage from "@/pages/public/Platform";
import RegionalCompliancePublic from "@/pages/public/RegionalCompliancePublic";
import Industries from "@/pages/public/Industries";
import MultiLocation from "@/pages/public/MultiLocation";
import Resources from "@/pages/public/Resources";
import DownloadApp from "@/pages/public/DownloadApp";
import AuditCheck from "@/pages/public/AuditCheck";
import { IkMatPage, HaccpAppPage, SfbbPage, TemperatureLogPage } from "@/pages/public/LandingPages";
import { BlogIndex, BlogArticle } from "@/pages/public/Blog";
import Units from "./pages/Units";
import Templates from "./pages/Templates";
import TemplateEditor from "./pages/TemplateEditor";
import Checklists from "./pages/Checklists";
import Incidents from "./pages/Incidents";
import Reports from "./pages/Reports";
import ChecklistExecutionModern from "./pages/ChecklistExecutionModern";
import ChecklistPrint from "./pages/ChecklistPrint";
import NewChecklist from "./pages/NewChecklist";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import DataProtection from "./pages/DataProtection";
import { CrystalTemplates } from "./pages/CrystalTemplates";
// Enterprise pages
import LawPacks from "./pages/LawPacks";
import LawPackImport from "./pages/LawPackImport";
import LawPackUpdatesManage from "./pages/LawPackUpdatesManage";
import ComplianceSettings from "./pages/ComplianceSettings";
import SystemPreferences from "./pages/SystemPreferences";
// Sites merged into /units, CorrectiveActions merged into /incidents
import AuditTrail from "./pages/AuditTrail";
import ExportCenter from "./pages/ExportCenter";
import Signatures from "./pages/Signatures";
import SignatureRequest from "./pages/SignatureRequest";
import UserManagement from "./pages/UserManagement";
// RoleAccess merged into /user-management
import SiteOverrides from "./pages/SiteOverrides";
import LegalDisclaimers from "./pages/LegalDisclaimers";
import ComplianceStatus from "./pages/ComplianceStatus";
import RiskAssessment from "./pages/features/RiskAssessment";
import OperationalIntelligence from "./pages/features/OperationalIntelligence";
import AutomatedScheduling from "./pages/features/AutomatedScheduling";
import Implementation from "./pages/services/Implementation";
import Training from "./pages/services/Training";
import Compliance from "./pages/services/Compliance";
import LanguagePacks from "./pages/LanguagePacks";
import QuickSetup from "./pages/QuickSetup";
import EmergencyActions from "./pages/EmergencyActions";
import Profile from "./pages/Profile";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAInstallBanner />
        <PWAUpdateNotification />
        <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/regional-compliance" element={<RegionalCompliancePublic />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/multi-location" element={<MultiLocation />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/download-app" element={<DownloadApp />} />
            <Route path="/audit-check" element={<AuditCheck />} />
            <Route path="/ik-mat" element={<IkMatPage />} />
            <Route path="/haccp-app" element={<HaccpAppPage />} />
            <Route path="/sfbb-alternative" element={<SfbbPage />} />
            <Route path="/temperature-log" element={<TemperatureLogPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route 
              path="/onboarding" 
              element={
                <ProtectedRoute allowExpired>
                  <Onboarding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/units" 
              element={
                <ProtectedRoute>
                  <Units />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/crystal-templates" 
              element={
                <ProtectedRoute>
                  <CrystalTemplates />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/templates" 
              element={
                <ProtectedRoute>
                  <Templates />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/templates/:id" 
              element={
                <ProtectedRoute>
                  <TemplateEditor />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checklists" 
              element={
                <ProtectedRoute>
                  <Checklists />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checklists/new" 
              element={
                <ProtectedRoute>
                  <NewChecklist />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checklists/:checklistId/execute" 
              element={
                <ProtectedRoute>
                  <ChecklistExecutionModern />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checklists/:checklistId/print" 
              element={
                <ProtectedRoute>
                  <ChecklistPrint />
                </ProtectedRoute>
              } 
            />
            {/* Legacy checklist execution route - redirects to new route */}
            <Route 
              path="/checklist-execution/:checklistId" 
              element={
                <ProtectedRoute>
                  <ChecklistExecutionModern />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/incidents" 
              element={
                <ProtectedRoute>
                  <Incidents />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              } 
            />
            {/* Enterprise Routes */}
            <Route 
              path="/quick-setup" 
              element={
                <ProtectedRoute allowExpired>
                  <QuickSetup />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/emergency-actions" 
              element={
                <ProtectedRoute>
                  <EmergencyActions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            {/* Legacy /setup route consolidated into /quick-setup */}
            <Route 
              path="/setup"
              element={
                <ProtectedRoute allowExpired>
                  <QuickSetup />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/law-packs" 
              element={
                <ProtectedRoute>
                  <LawPacks />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/law-packs/updates"
              element={
                <ProtectedRoute>
                  <LawPackUpdatesManage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/law-pack-import" 
              element={
                <ProtectedRoute>
                  <LawPackImport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/compliance-settings" 
              element={
                <ProtectedRoute>
                  <ComplianceSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/system-preferences" 
              element={
                <ProtectedRoute>
                  <SystemPreferences />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/language-packs" 
              element={
                <ProtectedRoute>
                  <LanguagePacks />
                </ProtectedRoute>
              } 
            />
            <Route path="/sites" element={<Navigate to="/units" replace />} />
            <Route path="/corrective-actions" element={<Navigate to="/incidents" replace />} />
            <Route 
              path="/audit-trail" 
              element={
                <ProtectedRoute>
                  <AuditTrail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/export-center" 
              element={
                <ProtectedRoute>
                  <ExportCenter />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/signatures" 
              element={
                <ProtectedRoute>
                  <Signatures />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/signature-request" 
              element={
                <ProtectedRoute>
                  <SignatureRequest />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/user-management" 
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route path="/role-access" element={<Navigate to="/user-management" replace />} />
            <Route 
              path="/site-overrides" 
              element={
                <ProtectedRoute>
                  <SiteOverrides />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/legal-disclaimers" 
              element={
                <ProtectedRoute>
                  <LegalDisclaimers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/compliance-status" 
              element={
                <ProtectedRoute>
                  <ComplianceStatus />
                </ProtectedRoute>
              } 
            />
            {/* Feature Routes */}
            <Route path="/features/risk-assessment" element={<RiskAssessment />} />
            <Route path="/features/operational-intelligence" element={<OperationalIntelligence />} />
            <Route path="/features/automated-scheduling" element={<AutomatedScheduling />} />
            
            {/* Service Routes */}
            <Route path="/services/implementation" element={<Implementation />} />
            <Route path="/services/training" element={<Training />} />
            <Route path="/services/compliance" element={<Compliance />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
